import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Seo } from "../components/Seo";
import { ContactForm } from "../components/ContactForm";
import { SERVICES, getService } from "../data/services";
import { SERVICE_FAQ, SERVICE_SEO } from "../data/seo";
import { stagesForPractice } from "../data/lifecycle";

const RELATED_INTRO = {
  heor: "HEOR rarely stands alone. Most engagements draw on our",
  rwe: "Real-world evidence is strongest when it is designed alongside our",
  "medical-affairs": "Medical strategy gains precision when it is informed by our",
  "competitive-intelligence": "Competitive intelligence changes more decisions when it is combined with our",
  "advanced-analytics": "Our analytics team most often works inside engagements led by our",
  "scientific-communications": "Publication planning delivers most when it is sequenced with our",
  "social-listening": "Patient-voice evidence is typically triangulated with our",
  "hcp-intelligence": "HCP intelligence is most actionable when paired with our",
  "us-market-access": "US access strategy is usually built on evidence from our",
  "eu-jca": "JCA preparation draws heavily on our",
};

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/" replace />;
  const faq = SERVICE_FAQ[slug] || [];
  const related = [...new Set(stagesForPractice(slug).flatMap((s) => s.practices))].filter((p) => p !== slug).slice(0, 3).map(getService);
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Service", name: service.name, serviceType: SERVICE_SEO[slug]?.title, description: SERVICE_SEO[slug]?.description || service.intro, provider: { "@type": "Organization", name: "MarksMan Healthcare", url: window.location.origin }, areaServed: ["US", "EU", "GB", "JP", "IN", "SG", "AU"], audience: { "@type": "BusinessAudience", audienceType: service.department } },
    faq.length && { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  ].filter(Boolean);

  return (
    <div data-testid={`service-page-${slug}`}>
      <Seo title={service.name} description={service.intro} jsonLd={jsonLd} breadcrumbs={[{ name: "Home", path: "/" }, { name: "Capabilities", path: "/" }, { name: service.name, path: `/services/${slug}` }]} />
      <section className="border-b border-slate-200 bg-[var(--surface)]">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24">
          <div className="lg:col-span-8">
            <div className="eyebrow rise">Capabilities / {service.name}</div>
            <div className="rise mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">For {service.department}</div>
            <h1 className="rise rise-1 mt-5 text-4xl font-extrabold leading-[1.1] text-[var(--navy)] sm:text-5xl">{service.headline}</h1>
            <p className="rise rise-2 mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">{service.intro}</p>
            <div className="rise rise-3 mt-8 flex flex-wrap gap-3">
              <a href="#enquire" data-testid="service-cta" className="btn-primary">Discuss your project <ArrowRight size={15} /></a>
              <Link to="/publications" className="btn-outline">See related publications</Link>
            </div>
          </div>
          <div className="grid gap-3 self-end lg:col-span-4">
            {service.stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="font-display text-3xl font-bold text-[var(--teal)]">{s.value}</div>
                <div className="mt-1 text-sm text-slate-600">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8" data-testid="service-advisory">
        <div className="lg:col-span-5">
          <div className="eyebrow">Where we start</div>
          <h2 className="mt-3 text-2xl font-bold text-[var(--navy)] sm:text-3xl">Strategic advisory</h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">Before any deliverable is scoped, our consultants work with your leadership to answer the questions that determine whether the evidence will change a decision.</p>
        </div>
        <ul className="grid gap-3 lg:col-span-7">
          {service.advisory.map((a) => (
            <li key={a} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-[var(--surface)] p-4 text-sm font-medium text-[var(--navy)]"><span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[var(--teal)]" />{a}</li>
          ))}
        </ul>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="eyebrow">Then we deliver</div>
        <h2 className="mt-3 text-2xl font-bold text-[var(--navy)] sm:text-3xl">Evidence generation capabilities</h2>
        <div className={`mt-10 grid gap-4 sm:grid-cols-2 ${service.offerings.length % 4 === 0 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          {service.offerings.map((o) => (
            <div key={o.title} className={`card ${service.offerings.length % 4 === 0 ? "!p-5" : ""}`}>
              <CheckCircle2 className="text-[var(--teal)]" size={20} />
              <h3 className={`mt-4 font-semibold text-[var(--navy)] ${service.offerings.length % 4 === 0 ? "text-base" : "text-lg"}`}>{o.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{o.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {service.spotlight && (
        <section id="spotlight" data-testid="service-spotlight" className="border-t border-slate-200">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <div className="eyebrow">{service.spotlight.eyebrow}</div>
                <h2 className="mt-3 text-2xl font-bold leading-tight text-[var(--navy)] sm:text-3xl lg:text-4xl">{service.spotlight.title}</h2>
                <p className="mt-5 text-base leading-relaxed text-slate-600">{service.spotlight.intro}</p>
                <div className="mt-8">
                  <div className="label">Sources we listen to</div>
                  <div className="flex flex-wrap gap-1.5">{service.spotlight.sources.map((s) => <span key={s} className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs text-slate-600">{s}</span>)}</div>
                </div>
              </div>
              <div className="grid gap-4 lg:col-span-7">
                {service.spotlight.pillars.map((p) => (
                  <div key={p.title} className="card flex gap-4 !p-5"><span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-[var(--teal)]" /><div><h3 className="text-lg font-semibold text-[var(--navy)]">{p.title}</h3><p className="mt-1.5 text-sm leading-relaxed text-slate-600">{p.desc}</p></div></div>
                ))}
              </div>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-[var(--surface)] p-6">
                <div className="label">How we do it</div>
                <ol className="space-y-2">{service.spotlight.method.map((m, i) => <li key={m} className="flex gap-3 text-sm text-slate-700"><span className="font-mono text-xs text-[var(--teal)]">0{i + 1}</span>{m}</li>)}</ol>
              </div>
              <div className="rounded-xl border border-slate-200 bg-[var(--surface)] p-6">
                <div className="label">What you receive</div>
                <ul className="space-y-2">{service.spotlight.deliverables.map((d) => <li key={d} className="flex gap-3 text-sm text-slate-700"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[var(--teal)]" />{d}</li>)}</ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {service.intelligence?.length > 0 && (
        <section id="intelligence" className="border-t border-slate-200 bg-[var(--navy-2)] text-white" data-testid="service-intelligence">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8">
            <div className="lg:col-span-4">
              <div className="eyebrow !text-teal-300">Where it adds value</div>
              <h2 className="mt-3 text-2xl font-bold sm:text-3xl">AI-assisted strategic analysis</h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">Not every project needs it. For the engagements below, our consultants draw on in-house AI tools to analyse faster and more consistently — and you receive the insight as a strategic report with recommendations.</p>
            </div>
            <div className="grid gap-4 lg:col-span-8 sm:grid-cols-2">
              {service.intelligence.map((i) => (
                <div key={i.title} className="rounded-xl border border-white/10 bg-white/5 p-6"><h3 className="text-lg font-semibold">{i.title}</h3><p className="mt-2 text-sm leading-relaxed text-slate-300">{i.desc}</p></div>
              ))}
            </div>
          </div>
        </section>
      )}

      {faq.length > 0 && (
        <section className="border-t border-slate-200" data-testid="service-faq">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8">
            <div className="lg:col-span-4">
              <div className="eyebrow">Frequently asked</div>
              <h2 className="mt-3 text-2xl font-bold text-[var(--navy)] sm:text-3xl">Questions clients ask about {service.name}</h2>
              {related.length > 0 && <p className="mt-6 text-sm leading-relaxed text-slate-600">{RELATED_INTRO[slug]} {related.map((r, i) => <span key={r.slug}>{i > 0 && (i === related.length - 1 ? " and " : ", ")}<Link to={`/services/${r.slug}`} data-testid={`related-${r.slug}`} className="font-semibold text-[var(--teal)] hover:underline">{r.name}</Link></span>)} capabilities.</p>}
            </div>
            <div className="divide-y divide-slate-200 lg:col-span-8">
              {faq.map((f) => (
                <details key={f.q} className="group py-4" data-testid="faq-item">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-[var(--navy)]"><span>{f.q}</span><span className="mt-1 shrink-0 text-[var(--teal)] transition-transform duration-200 group-open:rotate-45">+</span></summary>
                  <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-y border-slate-200 bg-[var(--surface)]">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="eyebrow">Other capabilities</div>
          {(() => {
            const others = SERVICES.filter((s) => s.slug !== slug);
            const half = Math.ceil(others.length / 2);
            return [others.slice(0, half), others.slice(half)].map((row, i) => (
              <div key={i} className={`grid gap-2 sm:grid-cols-3 lg:flex ${i === 0 ? "mt-6" : "mt-2"}`}>
                {row.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} className="flex flex-1 items-center justify-center rounded-full border border-slate-300 bg-white px-3 py-2 text-center text-xs font-medium leading-tight text-[var(--navy)] transition-colors duration-200 hover:border-[var(--teal)] hover:text-[var(--teal)] sm:text-[13px]">{s.name}</Link>
                ))}
              </div>
            ));
          })()}
        </div>
      </section>

      <section id="enquire" className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <div className="eyebrow">Get in touch</div>
          <h2 className="mt-3 text-2xl font-bold text-[var(--navy)] sm:text-3xl">Scope a {service.name} engagement</h2>
          <p className="mt-4 text-base text-slate-600">Share your objectives, timelines and target markets. We respond within one business day with next steps.</p>
        </div>
        <div className="lg:col-span-7"><ContactForm sourcePage={`service:${slug}`} defaultService={service.name} /></div>
      </section>
    </div>
  );
}
